/* Formatting: money, time, durations, ids. One place so every page agrees. */

const usd2 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Summaries: $14.17. */
export function money(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return usd2.format(v);
}

/** Per-row costs, where per-second billing makes cents too coarse: $0.0246. */
export function moneyFine(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  if (v === 0) return '$0.00';
  if (Math.abs(v) >= 1) return usd2.format(v);
  if (Math.abs(v) < 0.0001) return '<$0.0001';
  return `$${v.toFixed(4)}`;
}

/** The per-second price, to three significant figures: $0.000217. */
export function perSecond(usdPerSecond: number): string {
  return `$${Number(usdPerSecond.toPrecision(3))}`;
}

export function hours(seconds: number): string {
  const h = seconds / 3600;
  if (h === 0) return '0 h';
  if (h < 0.1) return `${Math.max(1, Math.round(seconds / 60))} min`;
  if (h < 10) return `${h.toFixed(2)} h`;
  if (h < 100) return `${h.toFixed(1)} h`;
  return `${Math.round(h).toLocaleString('en-US')} h`;
}

/** 38s, 4m 12s, 1h 03m. */
export function duration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms < 0 || Number.isNaN(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${String(m % 60).padStart(2, '0')}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

/** A spot in a recording: 0:12, 14:05, 1:02:09. */
export function timecode(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** 10 min, 1 h, 24 h: for timeouts. */
export function span(ms: number): string {
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m} min`;
  const h = m / 60;
  return Number.isInteger(h) ? `${h} h` : `${h.toFixed(1)} h`;
}

const dateFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const dateYearFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
const fullFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });
const monthFmt = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

export function fullTime(ms: number | null | undefined): string {
  return ms ? fullFmt.format(ms) : '—';
}

export function shortDate(ms: number): string {
  const d = new Date(ms);
  return d.getFullYear() === new Date().getFullYear() ? dateFmt.format(d) : dateYearFmt.format(d);
}

export function clock(ms: number): string {
  return timeFmt.format(ms);
}

/** "2026-10" → "October 2026". */
export function monthName(period: string): string {
  const [y, m] = period.split('-').map(Number);
  if (!y || !m) return period;
  return monthFmt.format(Date.UTC(y, m - 1, 1));
}

/** "2026-10-21" → "Oct 21" in UTC, the way the plane buckets days. */
export function dayLabel(date: string, withYear = false): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC' }).format(Date.UTC(y, m - 1, d));
}

/** "just now", "4 min ago", "3 h ago", "Oct 21". */
export function ago(ms: number | null | undefined, now = Date.now()): string {
  if (!ms) return '—';
  const diff = now - ms;
  if (diff < 0) return inFuture(ms, now);
  const s = Math.floor(diff / 1000);
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d} d ago`;
  return shortDate(ms);
}

export function inFuture(ms: number, now = Date.now()): string {
  const diff = ms - now;
  if (diff <= 0) return 'now';
  const m = Math.round(diff / 60000);
  if (m < 1) return 'in <1 min';
  if (m < 60) return `in ${m} min`;
  const h = Math.floor(m / 60);
  /* An invite's week reads in days; a sandbox's hours keep their minutes. */
  if (h >= 48) return `in ${Math.round(m / 1440)} days`;
  return `in ${h} h ${String(m % 60).padStart(2, '0')} min`;
}

/** task_mvp12pocOd9c0WdqFtGnGR2P → task_mvp12p…GR2P */
export function shortId(id: string, head = 6, tail = 4): string {
  const i = id.indexOf('_');
  const prefix = i > 0 ? id.slice(0, i + 1) : '';
  const rest = id.slice(prefix.length);
  if (rest.length <= head + tail + 1) return id;
  return `${prefix}${rest.slice(0, head)}…${rest.slice(-tail)}`;
}

/** https://github.com/lumenlabs/lumen-ios(.git) → lumenlabs/lumen-ios */
export function repoName(url: string): string {
  return url.replace(/^https?:\/\/(www\.)?(github\.com|gitlab\.com|bitbucket\.org)\//, '').replace(/\.git$/, '');
}

/** What a credit grant is for, as a person reads it: `yc` is the YC deal, `free` the sign-up credit. */
export function grantLabel(reason: string): string {
  if (reason === 'free') return 'Sign-up credit';
  if (reason === 'yc' || reason.startsWith('yc-')) return 'YC deal';
  return reason.charAt(0).toUpperCase() + reason.slice(1).replace(/-/g, ' ');
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;
}

export function titleCase(s: string): string {
  return s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}
